import { type WhatsappMessage } from '../whatsappWebApi';
import { cn } from '@/shared/utils/utils';
import { FileText, MapPin, Download } from 'lucide-react';

interface MessageBubbleProps {
  msg: WhatsappMessage;
}

export function MessageBubble({ msg }: MessageBubbleProps) {
  const isMe = msg.fromMe;
  const time = msg.timestamp 
    ? new Date(msg.timestamp * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) 
    : '';

  const mediaUrl = msg.hasMedia ? `/api/whatsapp-web/media/${encodeURIComponent(msg.id)}` : null;

  return (
    <div className={cn("flex w-full mb-1 px-[9%] md:px-[5%]", isMe ? "justify-end" : "justify-start")}>
      <div 
        className={cn(
          "relative max-w-[85%] md:max-w-[75%] rounded-[7.5px] px-[9px] py-[6px] shadow-[0_1px_0.5px_rgba(11,20,26,.13)] flex flex-col group",
          isMe ? "bg-[#d9fdd3]" : "bg-white",
          !msg.hasMedia && "pb-[8px]" // Extra padding for pure text
        )}
      >
        {/* Tail */}
        {isMe ? (
          <span className="absolute top-0 -right-[8px] text-[#d9fdd3] block w-[8px] h-[13px]">
            <svg viewBox="0 0 8 13" width="8" height="13" className="">
              <path opacity=".13" d="M5.188 1H0v11.193l6.467-8.625C7.526 2.156 6.958 1 5.188 1z"></path>
              <path fill="currentColor" d="M5.188 0H0v11.193l6.467-8.625C7.526 1.156 6.958 0 5.188 0z"></path>
            </svg>
          </span>
        ) : (
          <span className="absolute top-0 -left-[8px] text-white block w-[8px] h-[13px]">
            <svg viewBox="0 0 8 13" width="8" height="13" className="">
              <path opacity=".13" fill="#0000000" d="M1.533 3.568 8 12.193V1H2.812C1.042 1 .474 2.156 1.533 3.568z"></path>
              <path fill="currentColor" d="M1.533 2.568 8 11.193V0H2.812C1.042 0 .474 1.156 1.533 2.568z"></path>
            </svg>
          </span>
        )}
        {/* Author name in group chats */}
        {!isMe && msg.author && !msg.author.includes(msg.id.split('@')[0]) && (
          <div className="text-xs font-medium text-info-text mb-1">
            {msg.author.split('@')[0]}
          </div>
        )}

        {/* Media Content */}
        {msg.hasMedia && mediaUrl && (
          <div className="mb-1 rounded overflow-hidden relative">
            {msg.type === 'image' && (
              <img src={mediaUrl} alt="Received Image" className="max-h-80 w-auto rounded object-contain bg-black/5" loading="lazy" />
            )}
            
            {msg.type === 'video' && (
              <div className="relative max-h-80 w-auto max-w-[300px] bg-black rounded flex items-center justify-center">
                <video src={mediaUrl} controls className="max-h-80 w-auto" />
              </div>
            )}
            
            {msg.type === 'document' && (
              <a 
                href={mediaUrl} 
                download 
                target="_blank" 
                rel="noreferrer"
                className="flex items-center gap-3 p-3 bg-black/5 rounded-md hover:bg-black/10 transition-colors"
              >
                <div className="p-2 bg-[#ff7b6b] rounded text-white"><FileText className="h-6 w-6" /></div>
                <div className="flex flex-col flex-1 min-w-0">
                  <span className="text-body-sm font-medium truncate">{msg.body || 'Document'}</span>
                  <span className="text-xs text-on-surface-variant">Click to download</span>
                </div>
                <Download className="h-4 w-4 text-[#54656f] mr-1" />
              </a>
            )}

            {msg.type === 'audio' || msg.type === 'ptt' ? (
              <div className="py-2 min-w-[250px]">
                <audio src={mediaUrl} controls className="w-full h-10" />
              </div>
            ) : null}
          </div>
        )}

        {/* Location Content */}
        {msg.type === 'location' && msg.location && (
          <div className="mb-1 rounded overflow-hidden">
            <a 
              href={`https://maps.google.com/?q=${msg.location.lat},${msg.location.lng}`} 
              target="_blank" 
              rel="noreferrer"
              className="block relative bg-[#e9edef] h-32 w-full max-w-[300px]"
            >
              <div className="absolute inset-0 flex items-center justify-center">
                <MapPin className="h-8 w-8 text-danger-text" fill="currentColor" />
              </div>
              {msg.location.desc && (
                <div className="absolute bottom-0 left-0 right-0 bg-black/50 p-2 text-white text-xs truncate">
                  {msg.location.desc}
                </div>
              )}
            </a>
          </div>
        )}

        {/* Text Body */}
        {msg.body && (!msg.hasMedia || (msg.hasMedia && msg.type !== 'document' && msg.type !== 'audio' && msg.type !== 'ptt')) && (
          <div className="relative break-words text-[14.2px] leading-[19px] text-[#111b21]">
            <span dir="ltr" className="whitespace-pre-wrap">{msg.body}</span>
            <span className="inline-block w-[60px] h-[1px]"></span>
          </div>
        )}

        {/* Timestamp */}
        <div className="absolute bottom-[3px] right-[7px] flex items-center gap-[2px]">
          <span className="text-[11px] text-[#667781] leading-[15px] inline-block whitespace-nowrap mt-auto">
            {time}
          </span>
          {isMe && (
            <span className="inline-block ml-[2px]">
              <svg viewBox="0 0 16 11" width="16" height="11" className="text-[#53bdeb]">
                <path fill="currentColor" d="M11.8 1.6l-7.3 7.3-3.2-3.2-1.3 1.3 4.5 4.5 8.6-8.6z" />
                <path fill="currentColor" d="M15.8 1.6l-7.3 7.3-3.2-3.2-1.3 1.3 4.5 4.5 8.6-8.6z" transform="translate(3 0)" />
              </svg>
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
